import { db } from '@/db/client';
import { settings } from '@/db/schema';
import { eq } from 'drizzle-orm';
import * as SecureStore from 'expo-secure-store';

const AI_API_KEY_STORAGE_KEY = 'finapp_ai_api_key';

// ─── Generic Settings ────────────────────────────────────────

export async function getSetting(key: string): Promise<string | null> {
    const results = await db
        .select({ value: settings.value })
        .from(settings)
        .where(eq(settings.key, key));
    return results[0]?.value ?? null;
}

export async function setSetting(key: string, value: string): Promise<void> {
    // UPSERT numa instrução só. Ler-e-então-inserir era uma corrida clássica:
    // dois toggles disparados no mesmo instante (Sentry REACT-NATIVE-7, tela
    // /notifications/banks na 1.0.40) liam `null` juntos e o segundo INSERT
    // estourava `UNIQUE constraint failed: settings` — o usuário via a
    // preferência não salvar, sem entender por quê.
    const updatedAt = new Date().toISOString();
    await db
        .insert(settings)
        .values({ key, value, updatedAt })
        .onConflictDoUpdate({ target: settings.key, set: { value, updatedAt } });
}

async function deleteSetting(key: string): Promise<void> {
    await db.delete(settings).where(eq(settings.key, key));
}

// ─── AI Settings ─────────────────────────────────────────────

export type AiProvider = 'openai' | 'gemini' | 'openrouter';

export async function getAiProvider(): Promise<AiProvider> {
    const val = await getSetting('ai_provider');
    return (val as AiProvider) ?? 'gemini';
}

export async function setAiProvider(provider: AiProvider): Promise<void> {
    await setSetting('ai_provider', provider);
}

// Modelo escolhido pelo usuário no OpenRouter (ex.: "deepseek/deepseek-v3.2" ou um
// ":free"). Vazio = usar a cadeia padrão do app. Só existe para o OpenRouter: nos
// outros provedores o modelo é decisão nossa, aqui o catálogo inteiro é o atrativo.
const AI_OPENROUTER_MODEL_KEY = 'ai_openrouter_model';

export async function getAiOpenrouterModel(): Promise<string | null> {
    const value = await getSetting(AI_OPENROUTER_MODEL_KEY);
    return value?.trim() || null;
}

export async function setAiOpenrouterModel(model: string): Promise<void> {
    const normalized = model.trim();
    if (normalized) {
        await setSetting(AI_OPENROUTER_MODEL_KEY, normalized);
    } else {
        await deleteSetting(AI_OPENROUTER_MODEL_KEY);
    }
}

export async function getApiKey(): Promise<string | null> {
    // Sanitiza na LEITURA também, não só na escrita: quem já salvou a chave com um
    // invisível no meio (antes deste fix) continuaria batendo em 400 até apagar e
    // colar de novo — e não teria como enxergar o motivo.
    try {
        const secureValue = await SecureStore.getItemAsync(AI_API_KEY_STORAGE_KEY);
        if (secureValue) return sanitizeApiKey(secureValue);
    } catch {
        // SecureStore can be unavailable in some development environments.
    }

    const legacyValue = await getSetting('ai_api_key');
    if (legacyValue) {
        try {
            await SecureStore.setItemAsync(AI_API_KEY_STORAGE_KEY, legacyValue);
            await deleteSetting('ai_api_key');
        } catch {
            // Keep legacy fallback if SecureStore write fails.
        }
    }

    return legacyValue ? sanitizeApiKey(legacyValue) : legacyValue;
}

/**
 * Chave de API não contém espaço em lugar nenhum — nem nas pontas, nem no meio.
 * O `trim()` sozinho resolvia só as pontas, e a colagem no celular costuma trazer
 * quebra de linha ou caractere invisível (zero-width, NBSP) do meio do texto: o
 * Google devolve 400 API_KEY_INVALID e o usuário não tem como ver o que houve,
 * porque o caractere é invisível. Relatado com print em 02/ago/2026, com a chave
 * já no formato novo. Ver a mensagem `invalidKeyGeminiMalformed`.
 */
export function sanitizeApiKey(key: string): string {
    // \s cobre espaco, tab, quebra de linha e NBSP; o resto sao os invisiveis que
    // sobrevivem ao trim: zero-width space/non-joiner/joiner e BOM.
    return key.replace(/[\s\u200B\u200C\u200D\uFEFF]/g, '');
}

export async function setApiKey(key: string): Promise<void> {
    const normalizedKey = sanitizeApiKey(key);

    try {
        if (normalizedKey) {
            await SecureStore.setItemAsync(AI_API_KEY_STORAGE_KEY, normalizedKey);
        } else {
            await SecureStore.deleteItemAsync(AI_API_KEY_STORAGE_KEY);
        }
    } catch {
        // Keep settings-table fallback in environments where SecureStore is not available.
        if (normalizedKey) {
            await setSetting('ai_api_key', normalizedKey);
            return;
        }
    }

    await deleteSetting('ai_api_key');
}

export async function hasApiKey(): Promise<boolean> {
    const key = await getApiKey();
    return !!key && key.length > 0;
}

// ─── AI data-sharing consent ─────────────────────────────────
// Apple 5.1.1(i)/5.1.2(i): o usuário precisa autorizar explicitamente o envio
// dos dados ao provedor de IA externo ANTES de qualquer chamada que mande dados.
const AI_DATA_CONSENT_KEY = 'ai_data_consent_at';

export async function getAiDataConsentAt(): Promise<string | null> {
    return getSetting(AI_DATA_CONSENT_KEY);
}

export async function hasAiDataConsent(): Promise<boolean> {
    const value = await getSetting(AI_DATA_CONSENT_KEY);
    return !!value;
}

export async function setAiDataConsent(granted: boolean): Promise<void> {
    if (granted) {
        await setSetting(AI_DATA_CONSENT_KEY, new Date().toISOString());
    } else {
        // "Revogar" na tela da IA revoga TUDO que manda dado pro provedor, inclusive a
        // autorização separada da categorização de fatura.
        await deleteSetting(AI_DATA_CONSENT_KEY);
        await deleteSetting(AI_CATEGORIZATION_CONSENT_KEY);
    }
}

// Autorização SEPARADA pra IA categorizar linhas de fatura (ADR 0145). Não reaproveita a
// de cima de propósito: aquela foi dada diante de um texto que promete que "descrições de
// lançamento e nomes de estabelecimentos" nunca saem do aparelho — e é exatamente isso que
// a categorização envia. Quem autorizou o resumo financeiro não autorizou isto.
const AI_CATEGORIZATION_CONSENT_KEY = 'ai_categorization_consent_at';

export async function hasAiCategorizationConsent(): Promise<boolean> {
    return !!(await getSetting(AI_CATEGORIZATION_CONSENT_KEY));
}

export async function setAiCategorizationConsent(granted: boolean): Promise<void> {
    if (granted) {
        await setSetting(AI_CATEGORIZATION_CONSENT_KEY, new Date().toISOString());
    } else {
        await deleteSetting(AI_CATEGORIZATION_CONSENT_KEY);
    }
}

// ─── Medição de uso anônima ──────────────────────────────────
// NÃO existe mais flag `analytics_enabled` aqui. A decisão de não ter toggle
// in-app é antiga e está em docs/ANALYTICS.md (base legal = legítimo interesse
// na LGPD; oposição via política do site e pelo controle nativo do Android de ID
// de publicidade). O que existia era a MECÂNICA guardada "caso um dia queira":
// `setAnalyticsEnabled` nunca teve um chamador em toda a história do repo, então
// a leitura respondia `true` para todo mundo, sempre — ao custo de uma consulta
// ao banco por evento. Foi ela que derrubou a medição da primeira tela em
// instalação nova (Sentry REACT-NATIVE-1H: `no such table: settings`, porque o
// evento dispara antes de as migrations rodarem). Se um dia o toggle for
// desejado, volta em três linhas — mas aí COM tela, não dormente.

// Marco "primeira transação" já enviado — garante disparo único por instalação.
const FIRST_TRANSACTION_TRACKED_KEY = 'first_transaction_tracked_at';

export async function getFirstTransactionTracked(): Promise<boolean> {
    return (await getSetting(FIRST_TRANSACTION_TRACKED_KEY)) !== null;
}

export async function markFirstTransactionTracked(): Promise<void> {
    await setSetting(FIRST_TRANSACTION_TRACKED_KEY, new Date().toISOString());
}

const TRIAL_START_TRACKED_KEY = 'trial_start_tracked_at';

export async function getTrialStartTracked(): Promise<boolean> {
    return (await getSetting(TRIAL_START_TRACKED_KEY)) !== null;
}

export async function markTrialStartTracked(): Promise<void> {
    await setSetting(TRIAL_START_TRACKED_KEY, new Date().toISOString());
}

const ONBOARDING_COMPLETE_TRACKED_KEY = 'onboarding_complete_tracked_at';

export async function getOnboardingCompleteTracked(): Promise<boolean> {
    return (await getSetting(ONBOARDING_COMPLETE_TRACKED_KEY)) !== null;
}

export async function markOnboardingCompleteTracked(): Promise<void> {
    await setSetting(ONBOARDING_COMPLETE_TRACKED_KEY, new Date().toISOString());
}

const FIRST_STEPS_COMPLETE_TRACKED_KEY = 'first_steps_complete_tracked_at';

export async function getFirstStepsCompleteTracked(): Promise<boolean> {
    return (await getSetting(FIRST_STEPS_COMPLETE_TRACKED_KEY)) !== null;
}

export async function markFirstStepsCompleteTracked(): Promise<void> {
    await setSetting(FIRST_STEPS_COMPLETE_TRACKED_KEY, new Date().toISOString());
}

// Marco "segunda transação" resolvido — enviado, ou dispensado porque a instalação já
// passou de 2 transações antes de o marco existir (ver trackSecondTransactionOnce).
const SECOND_TRANSACTION_TRACKED_KEY = 'second_transaction_tracked_at';

export async function getSecondTransactionTracked(): Promise<boolean> {
    return (await getSetting(SECOND_TRANSACTION_TRACKED_KEY)) !== null;
}

export async function markSecondTransactionTracked(): Promise<void> {
    await setSetting(SECOND_TRANSACTION_TRACKED_KEY, new Date().toISOString());
}

const CARD_CREATED_TRACKED_KEY = 'card_created_tracked_at';

export async function getCardCreatedTracked(): Promise<boolean> {
    return (await getSetting(CARD_CREATED_TRACKED_KEY)) !== null;
}

export async function markCardCreatedTracked(): Promise<void> {
    await setSetting(CARD_CREATED_TRACKED_KEY, new Date().toISOString());
}
